import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Clock, XCircle, ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/InfoSections";
import { PlatePreview } from "@/components/PlatePreview";
import { api, clearCart } from "@/lib/api";
import { useI18n } from "@/i18n";

const COLOR_HEX = { white: "#FFFFFF", blue: "#2563EB", pink: "#EC4899", red: "#EF4444" };
const MAX_ATTEMPTS = 15;
const ICONS = { checking: Loader2, paid: CheckCircle2, pending: Clock, failed: XCircle, missing: XCircle };

export default function PaymentSuccess() {
  const { t, money } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  const [order, setOrder] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stopped) return;
        setOrder(data.order);
        if (data.payment_status === "paid") {
          setState("paid");
          clearCart();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setState("missing");
          return;
        }
      }
      if (attempts >= MAX_ATTEMPTS) {
        setState("pending");
        return;
      }
      timer.current = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer.current);
    };
  }, [sessionId]);

  const [title, text] = t(`success.${state}`);
  const Icon = ICONS[state];
  const accent = state === "paid" ? "bg-[var(--rpm-lime)]" : state === "failed" || state === "missing" ? "bg-red-100" : "bg-zinc-100";

  return (
    <div className="min-h-screen bg-white">
      <Header minimal />
      <main className="rpm-grid-bg py-14 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="rpm-card mx-4 overflow-hidden sm:mx-auto sm:max-w-xl" data-testid="payment-success-card">
          <div className="p-7 sm:p-9">
            <span className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${accent}`}>
              <Icon className={`h-7 w-7 ${state === "checking" ? "animate-spin" : ""}`} />
            </span>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="payment-success-status" data-state={state}>
              {title}
            </h1>
            <p className="mt-3 text-zinc-700">{text}</p>
            {order && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-black px-3 py-1 font-mono-rpm text-sm" data-testid="payment-order-number">
                {t("success.order")} {order.order_number}
              </div>
            )}
          </div>

          {order && (
            <div className="border-t border-zinc-200 bg-zinc-50 p-7 sm:p-9">
              <ul className="space-y-4">
                {order.items.map((it, i) => (
                  <li key={i} className="flex gap-4" data-testid={`payment-order-item-${i}`}>
                    <div className="w-28 shrink-0">
                      <PlatePreview type={it.product_id === "invisible" ? "invisible" : "individual"} top={it.top_text || ""} bottom={it.bottom_text} color={COLOR_HEX[it.text_color]} hiddenLabel={t("config.holderHidden")} className="w-full" />
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="flex justify-between gap-2 font-display font-semibold">
                        <span>
                          {it.name} × {it.quantity}
                        </span>
                        <span>{money(it.line_total)}</span>
                      </div>
                      {it.top_text && (
                        <div className="text-zinc-600">
                          {t("checkout.topShort")}: <span className="font-mono-rpm text-black">{it.top_text}</span>
                        </div>
                      )}
                      {it.bottom_text && (
                        <div className="text-zinc-600">
                          {t("checkout.bottomShort")}: <span className="font-mono-rpm text-black">{it.bottom_text}</span>
                        </div>
                      )}
                      {it.text_color && (
                        <div className="text-zinc-600">
                          {t("checkout.color")}: {t(`colors.${it.text_color}`)}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="rpm-dashline my-5" />
              {order.discount > 0 && (
                <div className="mb-2 flex justify-between text-sm font-medium text-[var(--rpm-orange)]" data-testid="payment-order-discount">
                  <span>{t("checkout.discount")}</span>
                  <span>−{money(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{t(`delivery.${order.delivery.method}.label`)}</span>
                <span>{order.delivery.price ? money(order.delivery.price) : t("checkout.free")}</span>
              </div>
              <div className="mt-2 flex items-end justify-between">
                <span className="font-display font-semibold">{t("checkout.total")}</span>
                <span className="font-display text-2xl font-bold" data-testid="payment-order-total">
                  {money(order.total)}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-zinc-200 p-7 sm:flex-row sm:p-9">
            {state === "failed" ? (
              <Link to="/#order" className="rpm-btn rpm-btn-orange flex-1" data-testid="payment-retry-button">
                {t("success.retry")}
              </Link>
            ) : (
              <Link to="/" className="rpm-btn rpm-btn-outline flex-1" data-testid="payment-home-button">
                <ArrowLeft className="h-4 w-4" /> {t("success.home")}
              </Link>
            )}
            {state === "paid" && (
              <Link to="/#order" className="rpm-btn rpm-btn-lime flex-1" data-testid="payment-new-order-button">
                {t("success.again")}
              </Link>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
